import { useMutation, useQuery } from "@apollo/client/react"
import { useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { PuzzleStatus } from "~/__generated__/graphql"
import { useDocumentTitle } from "~/common/use-document-title"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Badge } from "~/ui/badge"
import { Button } from "~/ui/button"
import { Card, CardContent } from "~/ui/card"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { Tabs, TabsList, TabsTrigger } from "~/ui/tabs"

const inputClass =
  "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
const textareaClass =
  "min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"

const puzzlesQuery = gql(/* GraphQL */ `
  query InternalAdminPuzzles($status: PuzzleStatus, $search: String) {
    internalAdminPuzzles(first: 50, status: $status, search: $search) {
      nodes {
        id
        wpId
        title
        brand
        pieceCount
        description
        status
        registeredOn
      }
    }
  }
`)

const puzzleUpdateMutation = gql(/* GraphQL */ `
  mutation InternalAdminPuzzleUpdate($input: InternalAdminPuzzleUpdateInput!) {
    internalAdminPuzzleUpdate(input: $input) {
      puzzle {
        id
        title
        brand
        pieceCount
        description
        status
      }
    }
  }
`)

const puzzleRegenerateQrMutation = gql(/* GraphQL */ `
  mutation InternalAdminPuzzleRegenerateQrCode($input: InternalAdminPuzzleRegenerateQrCodeInput!) {
    internalAdminPuzzleRegenerateQrCode(input: $input) {
      puzzle {
        id
      }
    }
  }
`)

type FilterValue = "ALL" | PuzzleStatus

type Puzzle = {
  id: string
  wpId: string
  title: string
  brand: string
  pieceCount: number
  description?: string | null
  status: PuzzleStatus
  registeredOn: string
}

const filters: Array<{ value: FilterValue; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "REMOVED", label: "Removed" },
]

const statusBadgeVariant = (status: PuzzleStatus) => {
  switch (status) {
    case "ACTIVE":
      return "default"
    case "INACTIVE":
      return "secondary"
    case "REMOVED":
      return "outline"
    default:
      throw status satisfies never
  }
}

export const InternalAdminPuzzlesScreen = () => {
  useDocumentTitle("Puzzles")
  const [filter, setFilter] = useState<FilterValue>("ALL")
  const [search, setSearch] = useState("")
  const { data, loading, error } = useQuery(puzzlesQuery, {
    variables: { status: filter === "ALL" ? null : filter, search: search.trim() || null },
  })
  const [editing, setEditing] = useState<Puzzle | null>(null)

  const puzzles = (data?.internalAdminPuzzles.nodes ?? []) as Puzzle[]

  return (
    <TablePageLayout>
      <h1 className="text-2xl">Puzzles</h1>
      <hr className="my-4 border-0 border-b" />

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterValue)}>
          <TabsList>
            {filters.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <input
          placeholder="Search wp_id, title, or brand"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputClass} w-72`}
        />
      </div>

      <div className="mt-6">
        {!data && loading ? (
          <ScreenLoading />
        ) : error ? (
          <GraphqlError error={error} />
        ) : puzzles.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-gray-600">
              No puzzles match.
            </CardContent>
          </Card>
        ) : (
          <ul className="grid gap-3">
            {puzzles.map((p) => (
              <li key={p.id}>
                <Card>
                  <CardContent className="flex items-start justify-between gap-4 py-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{p.title}</span>
                        <Badge variant="secondary" className="font-mono text-[10px]">
                          {p.wpId}
                        </Badge>
                        <Badge variant={statusBadgeVariant(p.status)}>{p.status}</Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        {p.brand} · {p.pieceCount.toLocaleString()} pieces
                      </div>
                      {p.description ? (
                        <p className="mt-1 line-clamp-2 text-sm text-gray-600">{p.description}</p>
                      ) : null}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditing(p)}>
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing ? (
        <EditDialog key={editing.id} puzzle={editing} onClose={() => setEditing(null)} />
      ) : null}
    </TablePageLayout>
  )
}

const EditDialog = ({ puzzle, onClose }: { puzzle: Puzzle; onClose: () => void }) => {
  const [updatePuzzle, { loading: saving }] = useMutation(puzzleUpdateMutation, {
    onError: () => null,
  })
  const [regenerateQr, { loading: regenerating }] = useMutation(puzzleRegenerateQrMutation, {
    onError: () => null,
  })

  const [title, setTitle] = useState(puzzle.title)
  const [brand, setBrand] = useState(puzzle.brand)
  const [pieceCount, setPieceCount] = useState(String(puzzle.pieceCount))
  const [description, setDescription] = useState(puzzle.description ?? "")
  const [status, setStatus] = useState<PuzzleStatus>(puzzle.status)

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit puzzle <span className="font-mono text-sm text-gray-500">{puzzle.wpId}</span>
          </DialogTitle>
        </DialogHeader>

        <form
          className="grid gap-3"
          onSubmit={async (e) => {
            e.preventDefault()
            const result = await updatePuzzle({
              variables: {
                input: {
                  id: puzzle.id,
                  puzzleInput: {
                    title,
                    brand,
                    pieceCount: Number(pieceCount),
                    description: description || null,
                    status,
                  },
                },
              },
            })
            if (result.data) {
              toast.success("Puzzle updated")
              onClose()
            }
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-gray-600">Title</span>
            <input
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-gray-600">Brand</span>
            <input
              className={inputClass}
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              required
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-gray-600">Piece count</span>
            <input
              className={inputClass}
              type="number"
              min={1}
              value={pieceCount}
              onChange={(e) => setPieceCount(e.target.value)}
              required
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-gray-600">Status</span>
            <select
              className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as PuzzleStatus)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="REMOVED">Removed</option>
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-gray-600">Description</span>
            <textarea
              className={textareaClass}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={regenerating}
              onClick={async () => {
                const result = await regenerateQr({
                  variables: { input: { id: puzzle.id } },
                })
                if (result.data) toast.success("QR regeneration enqueued")
              }}
            >
              Regenerate QR
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
